import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import {
  Link,
  ErrorBox,
  ErrorTitle,
  ErrorMessage,
} from '@marageti/z4-lib';
import SetPasswordForm from '../../components/set-password-form';
import SendResetLinkForm from '../../components/auth/email-reset-link-form';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso-logo-redesign.svg';
import { ActivateViewProps } from './types';
import './activate.scss';
import GoogleLoginView from '../../components/auth/google-sso-button';

const ActivateView = ({
  email,
  loginError = undefined,
  newLinkSent,
  onGoogleSSOSubmit,
  onPasswordSubmit,
  onResendLinkSuccess,
  tokenExpired,
}: ActivateViewProps): React.ReactElement => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Stack
      spacing={3}
      sx={{
        width: { mobile: '342px', desktop: '333px' },
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Stack flexDirection="column" spacing={4} alignItems="center">
        <Stack spacing={2} alignItems="center">
          <ZicassoSVG aria-label="Zicasso" />
          {!tokenExpired && (
            <>
              <Typography variant="h2">
                Set Your Password
              </Typography>
              <Typography color="text.secondary" style={{ textAlign: 'center' }}>
                Create a password to access Zicasso platform, contact specialists, and manage your trips.
              </Typography>
            </>
          )}
          {(tokenExpired && !newLinkSent) && (
          <>
            <Typography variant="h2">
              Oh, this link has expired!
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              This authentication link has expired for security reasons. Please resend and try again.
            </Typography>
            <Typography color="text.secondary">
              Email:
              {' '}
              <strong>{email}</strong>
            </Typography>
          </>
          )}
        </Stack>
        {!tokenExpired && (
        <SetPasswordForm email={email} onSubmit={onPasswordSubmit} />
        )}
        {(tokenExpired && !newLinkSent) && (
        <SendResetLinkForm email={email} onSuccess={onResendLinkSuccess} type="activate" />
        )}
      </Stack>
      <Link
        color="primary"
        sx={{ '&:hover': { color: 'secondary.main' } }}
        to="/login"
        underline="always"
        variant="subtitle1"
      >
        Go back to Sign In
      </Link>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Divider sx={{ flex: 1 }} />
        <Typography variant="body2" color="" margin="0 12px">or</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
      >
        <GoogleLoginView onSuccess={onGoogleSSOSubmit} />
      </Box>
      {loginError && (
        <ErrorBox>
          <ErrorTitle>
            There was an on our end.
          </ErrorTitle>
          {loginError.code ? (
            <ErrorMessage>
              {loginError.code && `Code: ${loginError.code} `}
              {loginError.message || 'Please contact support'}
              <br />
              {loginError.id && `ID: ${loginError.id}`}
            </ErrorMessage>
          ) : (
            <ErrorMessage>
              Please contact support.
            </ErrorMessage>
          )}
        </ErrorBox>
      )}
    </Stack>
  </Box>
);

export default ActivateView;
